/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect } from 'react'
import { cloneDeep } from 'lodash'
import { FormLabel } from 'react-bootstrap'
import { useServiceContext } from '../../../../root'
import { useModal } from '../../../../contexts/ModalContext'
import Modal from '../../../../components/Modal'
import FsiPicker from '../../../../components/FsiPicker/FsiPicker'
import type { TreeNode } from '../../../../components/FsiPickerNode'
import { EOS_CONFIG_LEVEL, EOS_CONFIG_METHOD } from '../../../../constants/eos'
import type { EosStep1ModalProps, EosConfig, EosOptions, EosConfigLevel, EosStudyState } from '../../../../types/eos'
import EosStudyInfo from './EosStudyInfo'
import EosStep1RolesError from './EosStep1RolesError'
import MissingSitesModal from './MissingSitesModal'

const EosSetUpStep1Modal: React.FC<EosStep1ModalProps> = ({ params }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const CONSTANT = context.constant.eos
  const USER_CONSTANT = context.constant.user
  const { showModal } = useModal()

  const [isOpen, setIsOpen] = useState(true)
  const [selection, setSelection] = useState<TreeNode | null>(null)
  const [noSelectionError, setNoSelectionError] = useState(false)
  const [rootFolderId, setRootFolderId] = useState<number | null>(null)
  const [studyInfoLoadedForFolders, setStudyInfoLoadedForFolders] = useState<string[]>([])
  const [options, setOptions] = useState<Partial<EosOptions>>(params?.options ?? {})
  const [config, setConfig] = useState<Partial<EosConfig>>(() => ({
    folder: null,
    selectedFolder: null,
    studyGroup: null,
    study: null,
    environment: null,
    method: EOS_CONFIG_METHOD.APP,
    level: EOS_CONFIG_LEVEL.STUDY,
    ...params?.config
  }))
  const [configStudyInfoUuids, setConfigStudyInfoUuids] = useState<Record<string, string>>({})
  const [isEditableStudyInfo, setIsEditableStudyInfo] = useState(false)
  const [isUserSponsorInstructionCoordinator, setIsUserSponsorInstructionCoordinator] = useState(false)
  const [isInstructionPresent, setIsInstructionPresent] = useState(false)
  const [stateSelection, setStateSelection] = useState<EosStudyState>('ACTIVE')
  const [isProceedingEnabled, setIsProceedingEnabled] = useState(false)
  const [concurrencyPollingInterval, setConcurrencyPollingInterval] = useState(0)

  useEffect(() => {
    const user = context.service.auth.getUser()
    const isSponsor = user?.role === USER_CONSTANT.roles.POWER_USER && user?.eosSponsorPowerUserPlus
    setIsUserSponsorInstructionCoordinator(!!isSponsor)

    if (options?.eosFolderId) {
      setRootFolderId(options.eosFolderId)
    } else {
      context.api.eos.getEosFolderId().then((data: any) => {
        setRootFolderId(data)
        setOptions((prev) => ({ ...prev, eosFolderId: data }))
      })
    }
  }, [])

  useEffect(() => {
    if (isUserSponsorInstructionCoordinator) {
      const featureInfo = context.service.init.getFeaturesInfo()
      setConcurrencyPollingInterval((featureInfo?.eosPollingInterval ?? 60) * 1000)
    }
  }, [isUserSponsorInstructionCoordinator])

  const isNodeSelectable = (node: TreeNode): boolean => {
    const eosData = node?.eosFilePicker as any
    return (
      node.id !== rootFolderId &&
      !!eosData?.mediaFolder &&
      eosData?.levelValue === 'Study' &&
      !eosData?.transferInProgress &&
      !(isUserSponsorInstructionCoordinator && eosData?.legacyStudy) &&
      !(isUserSponsorInstructionCoordinator && eosData?.studyBlockedForSelection)
    )
  }

  const isNodeExpandable = (node: TreeNode): boolean => {
    return !(node as any).eosMediaFolder || typeof (node as any).eosLevelValue === 'undefined'
  }

  const onFolderChange = (selectedStudyFolder: TreeNode) => {
    const eosData = selectedStudyFolder?.eosFilePicker as any
    if (
      isUserSponsorInstructionCoordinator &&
      eosData?.draftBeingEditedBy &&
      context.service.auth.getUserId() !== eosData.draftBeingEditedBy.id
    ) {
      context.service.toast.error(
        translate('INSTRUCTIONS_BEING_EDITED_ERROR', {
          userName: eosData.draftBeingEditedBy.name,
          userEmail: eosData.draftBeingEditedBy.emailId
        })
      )
      setIsProceedingEnabled(false)
    } else {
      setIsProceedingEnabled(true)
    }

    setIsInstructionPresent(!!(eosData?.draftBeingEditedBy || eosData?.studyBlockedForSelection))

    if (selectedStudyFolder && selectedStudyFolder.id) {
      setConfig((prev) => ({
        ...prev,
        selectedFolder: {
          id: selectedStudyFolder.id as number,
          name: selectedStudyFolder.name ?? ''
        }
      }))

      if ((selectedStudyFolder as any).studyInfo === false) {
        // folder has no study info
        setIsEditableStudyInfo(true)
        setConfigStudyInfoUuids({})
      } else if ((selectedStudyFolder as any).studyInfo) {
        // study info has been preloaded
        setIsEditableStudyInfo(false)
        setConfigStudyInfoUuids({ ...(selectedStudyFolder as any).studyInfo })
      } else if (!studyInfoLoadedForFolders.includes((selectedStudyFolder as any).eosStudyName ?? '')) {
        setStudyInfoLoadedForFolders((prev) => [...prev, (selectedStudyFolder as any).eosStudyName ?? ''])

        // load study info
        context.service.eosService!.loadStudyInfo(null, selectedStudyFolder.id as number).then(
          (data: any) => {
            setIsEditableStudyInfo(false)
            setConfigStudyInfoUuids({ ...data })
            ;(selectedStudyFolder as any).studyInfo = data
            setConfig((prev) => ({ ...prev, folder: selectedStudyFolder as any }))
          },
          () => {
            setIsEditableStudyInfo(true)
            ;(selectedStudyFolder as any).studyInfo = false
            setConfigStudyInfoUuids({})
            setConfig((prev) => ({ ...prev, folder: selectedStudyFolder as any }))
          }
        )
      }
    } else {
      setIsEditableStudyInfo(false)
    }
  }

  const configChangeHandler = (configData: Partial<EosConfig>) => {
    const merged = cloneDeep(config)
    Object.assign(merged, configData)
    if (merged.study && merged.folder?.studyInfo) {
      Object.assign(merged.study, { isLegacy: merged.folder.studyInfo.isLegacy })
    }
    setConfig(merged)
  }

  const isUserAdminRole = (): boolean => {
    return context.service.auth.getRole() === USER_CONSTANT.roles.ADMIN
  }

  const getRolesData = () => {
    context.api.eos
      .getRolesValidation(config?.study?.configurationTypeUuid ?? '')
      .then((data: any) => {
        if (!data.valid) {
          showModal(
            <EosStep1RolesError
              variables={{ cdName: config.studyGroup?.name ?? '', roles: data.roles }}
              config={config as EosConfig}
              options={options as EosOptions}
            />
          )
          doClose()
        } else {
          proceedToEosStep2()
        }
      })
      .catch((error: any) => {
        context.service.toast.error(error?.data?.message ?? 'EOS_ROLES_ERROR_MESSAGE')
      })
  }

  const proceedToEosStep2 = () => {
    const propsData = {
      config: config as EosConfig,
      options: options as EosOptions
    }

    if (config.level === EOS_CONFIG_LEVEL.STUDY && isUserAdminRole() && !isUserSponsorInstructionCoordinator) {
      showModal(
        <MissingSitesModal
          params={propsData}
          isUserSponsorInstructionCoordinator={isUserSponsorInstructionCoordinator}
          isInstructionPresent={isInstructionPresent}
          isStudyArchived={stateSelection === 'ARCHIVED'}
        />
      )
    } else {
      import('../step2/EosSetUpStep2').then(({ default: EosSetUpStep2 }) => {
        showModal(
          <EosSetUpStep2
            params={propsData}
            isUserSponsorInstructionCoordinator={isUserSponsorInstructionCoordinator}
            isInstructionPresent={isInstructionPresent}
            isStudyArchived={stateSelection === 'ARCHIVED'}
            concurrencyPollingInterval={concurrencyPollingInterval}
          />
        )
      })
    }
    doClose()
  }

  const doClose = () => {
    setIsOpen(false)
  }

  const handleSubmit = () => {
    if (selection) {
      if (config.studyGroup?.mccEnabled) {
        getRolesData()
      } else {
        proceedToEosStep2()
      }
    } else {
      setNoSelectionError(true)
    }
  }

  const fsiPickerOptions = {
    eosMediaTransferMode: true,
    rootId: rootFolderId,
    extendParams: {
      onlyFolders: false,
      onlyFoldersIncludeEosFolderTypeCheck: true
    },
    showRC: false,
    showSystem: false,
    showRadio: true,
    selector: 'folder' as const
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('EOS_SETUP_TITLE_USERS_WORKFLOWS')}
      size="lg"
      className="setup-users-and-wf-container"
    >
      <Modal.Body>
        <div className="fs12 setup-users-and-wf-container">
          <div className="step1-filter-wrapper">
            <label>{translate('STUDY_STATE')}</label>
            <div className="state-selection">
              <input
                className="state-radio"
                type="radio"
                name="state"
                value="ACTIVE"
                checked={stateSelection === 'ACTIVE'}
                onChange={() => setStateSelection('ACTIVE')}
              />
              <label className="state-value">{translate('ACTIVE')}</label>
            </div>
            <div className="state-selection">
              <input
                className="state-radio"
                type="radio"
                name="state"
                value="ARCHIVED"
                checked={stateSelection === 'ARCHIVED'}
                onChange={() => setStateSelection('ARCHIVED')}
              />
              <label className="state-value">{translate('ARCHIVED')}</label>
            </div>
          </div>

          {rootFolderId && (
            <div className="eos-study-selector">
              <FormLabel>{translate('EOS_SETUP_FOLDER')}</FormLabel>
              <FsiPicker
                options={fsiPickerOptions}
                onSelect={(node) => {
                  setSelection(node as TreeNode)
                  setNoSelectionError(false)
                  onFolderChange(node as TreeNode)
                }}
                isNodeSelectable={isNodeSelectable}
                isNodeExpandable={isNodeExpandable}
                stateSelection={stateSelection}
                isSetupUsersAndWorkflowsFlow={true}
                isUserSponsorInstructionCoordinator={isUserSponsorInstructionCoordinator}
              />
            </div>
          )}

          {noSelectionError && <div className="error fs12">{translate('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')}</div>}

          {selection && (
            <EosStudyInfo configUuids={configStudyInfoUuids} config={config} handleConfigChange={configChangeHandler} />
          )}
          {!isUserSponsorInstructionCoordinator && (
            <div className="assignment-level-wrapper">
              <label>{translate('USERS_AND_WORKFLOWS_ASSIGNMENT_LEVEL')}</label>
              <div className="level-selection d-flex">
                <input
                  type="radio"
                  name="level"
                  value="STUDY"
                  checked={config.level === EOS_CONFIG_LEVEL.STUDY}
                  onChange={() => setConfig((prev) => ({ ...prev, level: EOS_CONFIG_LEVEL.STUDY }))}
                  className="study-level-radio"
                />
                <label>{translate('EOS_SETUP_USERS_WORKFLOWS_CONFIG_LEVEL_STUDY')}</label>
              </div>
              <div className="level-selection d-flex">
                <input
                  type="radio"
                  name="level"
                  value="COUNTRY"
                  checked={config.level === EOS_CONFIG_LEVEL.COUNTRY}
                  onChange={() => setConfig((prev) => ({ ...prev, level: EOS_CONFIG_LEVEL.COUNTRY }))}
                  className="country-level-radio"
                />
                <label>{translate('EOS_SETUP_USERS_WORKFLOWS_CONFIG_LEVEL_COUNTRY')}</label>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Button variant="link" onClick={doClose}>
          {translate('CANCEL')}
        </Modal.Footer.Button>
        <Modal.Footer.Button variant="primary" onClick={handleSubmit} disabled={!isProceedingEnabled}>
          {translate('NEXT')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EosSetUpStep1Modal
